import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getOrders, 
  updateOrderStatus,
  getOrderStatistics 
} from '../../services/api';

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    date_from: '',
    date_to: '',
    disputed: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
    fetchOrderStats();
  }, [filters, pagination.page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
  
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });
  
      console.log('📦 Fetching orders with params:', params);
  
      const response = await getOrders(params);
      console.log('📦 Full API response:', response);
      
      // Handle different response structures
      let ordersData = [];
      let paginationData = {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      };
  
      if (response.data) {
        // Structure 1: { data: { orders: [], pagination: {} } }
        if (response.data.orders) {
          ordersData = response.data.orders;
          paginationData = response.data.pagination || paginationData;
        } 
        // Structure 2: { data: [] } - just array of orders
        else if (Array.isArray(response.data)) {
          ordersData = response.data;
          paginationData = {
            ...paginationData,
            total: response.data.length
          };
        }
        // Structure 3: { data: { data: [], pagination: {} } } - nested data
        else if (response.data.data && Array.isArray(response.data.data)) {
          ordersData = response.data.data;
          paginationData = response.data.pagination || paginationData;
        }
      } 
      // Structure 4: Direct array { orders: [], pagination: {} }
      else if (response.orders) {
        ordersData = response.orders;
        paginationData = response.pagination || paginationData;
      }
  
      console.log('📦 Processed orders:', ordersData);
      console.log('📦 Processed pagination:', paginationData);
  
      setOrders(ordersData);
      setPagination(prev => ({
        ...prev,
        ...paginationData
      }));
    } catch (err) {
      console.error('❌ Orders fetch error:', err);
      setError('Failed to load orders: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderStats = async () => {
    try {
      const response = await getOrderStatistics();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load order stats:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    if (!window.confirm(`Change order status to "${newStatus}"?`)) {
      return;
    }

    try {
      await updateOrderStatus(orderId, { status: newStatus });
      fetchOrders(); // Refresh the list
    } catch (err) {
      setError(`Failed to update order status: ` + err.message);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status, disputed = false) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    
    if (disputed) {
      return <span className={`${baseClasses} bg-red-100 text-red-800 border border-red-200`}>⚠️ Disputed</span>;
    }

    switch (status) {
      case 'pending':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Pending</span>;
      case 'offer_pending':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>Offer Pending</span>;
      case 'accepted':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Accepted</span>;
      case 'rejected':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>Rejected</span>;
      case 'cancelled':
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Cancelled</span>;
      case 'completed':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Completed</span>;
      case 'shipped':
        return <span className={`${baseClasses} bg-purple-100 text-purple-800`}>Shipped</span>;
      case 'delivered':
        return <span className={`${baseClasses} bg-indigo-100 text-indigo-800`}>Delivered</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{status}</span>;
    }
  };

  const getEscrowBadge = (escrowStatus) => {
    if (!escrowStatus) return null;
    
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    
    switch (escrowStatus) {
      case 'held':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>💰 Held</span>;
      case 'released':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>✅ Released</span>;
      case 'refunded':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>↩️ Refunded</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Escrow</span>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Order Management</h1>
            <p className="text-gray-600 mt-1">Manage and track all marketplace orders</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/admin/orders/disputes')}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              View Disputes
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overview?.total_orders || 0}</p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <span className="text-blue-600">📦</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Today: {stats.overview?.orders_today || 0}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">{stats.overview?.completed_orders || 0}</p>
              </div>
              <div className="bg-green-100 p-2 rounded-lg">
                <span className="text-green-600">✅</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Success rate: {stats.overview?.total_orders ? Math.round((stats.overview.completed_orders / stats.overview.total_orders) * 100) : 0}%
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Disputes</p>
                <p className="text-2xl font-bold text-red-600">{stats.overview?.disputed_orders || 0}</p>
              </div>
              <div className="bg-red-100 p-2 rounded-lg">
                <span className="text-red-600">⚠️</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Active: {stats.dispute_stats?.[0]?.active_disputes || 0}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Revenue</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(stats.overview?.total_order_value || 0)}
                </p>
              </div>
              <div className="bg-purple-100 p-2 rounded-lg">
                <span className="text-purple-600">💰</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Avg: {formatCurrency(stats.overview?.avg_order_value || 0)}
            </p>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-red-500">⚠️</span>
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-800"
          >
            ✕
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Order, buyer, seller, listing..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="offer_pending">Offer Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Disputed Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Dispute Status</label>
            <select
              value={filters.disputed}
              onChange={(e) => handleFilterChange('disputed', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Orders</option>
              <option value="true">Disputed Only</option>
            </select>
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
          <button
            onClick={() => setFilters({
              search: '',
              status: '',
              date_from: '',
              date_to: '',
              disputed: ''
            })}
            className="text-gray-600 hover:text-gray-800 text-sm font-medium"
          >
            Clear Filters
          </button>
          <button
            onClick={fetchOrders}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            🔄 Apply Filters
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order & Listing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parties
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <tr key={order.order_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        Order #{order.order_id}
                      </div>
                      <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                        {order.listing_title}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        {getEscrowBadge(order.escrow_status)}
                        {order.escrow_count > 0 && (
                          <span className="text-xs text-gray-500">
                            Escrow: {order.escrow_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div>
                        <div className="text-xs font-medium text-gray-500">Buyer</div>
                        <div className="text-sm text-gray-900">
                          {order.buyer_first_name} {order.buyer_last_name}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500">Seller</div>
                        <div className="text-sm text-gray-900">
                          {order.seller_store_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-green-600">
                      {formatCurrency(order.total_amount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      Item: {formatCurrency(order.listing_price)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      {getStatusBadge(order.status, order.disputed)}
                      <div className="text-xs text-gray-500">
                        {getTimeAgo(order.created_at)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(order.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => navigate(`/admin/orders/${order.order_id}`)}
                      className="text-blue-600 hover:text-blue-900 transition-colors"
                    >
                      View
                    </button>
                    {order.disputed && (
                      <button
                        onClick={() => navigate(`/admin/orders/disputes?order=${order.order_id}`)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        Resolve
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {orders.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> orders
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Orders;